import { useAuth } from "../../auth/AuthContext";
import { canManageFleet } from "../../auth/roleView";
import {
  useGovernanceFindings,
  useProjectGovernancePolicy,
  useResolveGovernanceFinding,
} from "../../api/queries";
import type { GovernanceFinding } from "../../api/types";
import { EmptyState } from "../../components/AsyncStates";
import { Button } from "../../components/Button";
import { QueryStates } from "../../components/QueryStates";
import { StatePill } from "../../components/StatePill";
import "./governance.css";

const PAGE_LIMIT = 50;

/** The one predicate id that turns an open finding from advisory into an actual promotion
 * blocker (ADR-0059/ADR-0058) — mirrors the server's `predRequireMaxOpenFindings`. */
const MAX_OPEN_FINDINGS_PREDICATE_ID = "promote.require.max_open_findings";

/** Shortens a git commit sha for display (mirrors GovernanceDecisionsPage's shortCommit). */
function shortCommit(sha: string): string {
  return sha.length > 12 ? sha.slice(0, 12) : sha;
}

function severityTone(severity: string): "negative" | "warning" | "neutral" {
  switch (severity) {
    case "critical":
      return "negative";
    case "warning":
      return "warning";
    default:
      return "neutral";
  }
}

function severityShape(severity: string): "triangle" | "bars" | "ring" {
  switch (severity) {
    case "critical":
      return "triangle";
    case "warning":
      return "bars";
    default:
      return "ring";
  }
}

function FindingRow({
  finding,
  canResolve,
  onResolve,
  resolving,
}: {
  finding: GovernanceFinding;
  canResolve: boolean;
  onResolve: (id: string) => void;
  resolving: boolean;
}) {
  const open = finding.status === "open";
  return (
    <li className="governance-finding" data-status={finding.status}>
      <div className="governance-finding__head">
        <StatePill label={finding.severity} tone={severityTone(finding.severity)} shape={severityShape(finding.severity)} />
        <span className="governance-finding__rule data-table__mono">{finding.ruleId}</span>
        <StatePill
          label={open ? "Open" : "Resolved"}
          tone={open ? "warning" : "positive"}
          shape={open ? "ring" : "dot"}
        />
      </div>
      <p className="governance-finding__detail">{finding.detail || "(no detail)"}</p>
      <div className="governance-finding__meta">
        <span className="data-table__mono">{finding.serviceId}</span>
        <span>→ {finding.targetEnvClass}</span>
        <span className="data-table__mono" title={finding.governanceCommit}>
          {shortCommit(finding.governanceCommit)}
        </span>
        <span className="data-table__secondary">{new Date(finding.producedAt).toLocaleString()}</span>
      </div>
      {finding.status === "resolved" && finding.resolvedBy && (
        <p className="governance-finding__resolution data-table__secondary">
          Resolved by {finding.resolvedBy}
          {finding.resolutionNote ? `: ${finding.resolutionNote}` : ""}
        </p>
      )}
      {open && canResolve && (
        <div className="governance-finding__actions">
          <Button
            variant="secondary"
            onClick={() => onResolve(finding.id)}
            disabled={resolving}
          >
            {resolving ? "Resolving…" : "Resolve / waive"}
          </Button>
        </div>
      )}
    </li>
  );
}

/**
 * The governing-agent FINDINGS surface for the Decisions view (ADR-0059): a project's open and
 * resolved findings — the objections a finding PRODUCER raised against promotion-scoped changes.
 *
 * It is the HUMAN half of the finding lifecycle. There is no create action here (a finding is
 * appended only by the server-side producer as the RoleGovernor system principal); the only
 * mutation is RESOLVE/waive, gated on `governance_finding:resolve` (operator/admin) both in the UI
 * (the button only renders for a manager) and — the real decision — server-side (S-8).
 *
 * The ADVISORY-VS-ENFORCED banner makes inertness VISIBLE (A-10) — and tells the TRUTH for the
 * SELECTED project rather than a static disclaimer (#571's introspect endpoint): it fetches the
 * project's actual declared policy and states plainly whether findings are enforced HERE, not
 * just that they could be somewhere. A reader must never mistake an advisory finding for an
 * enforced gate, so every state the policy fetch can be in — loading, failed, confirmed
 * default-off, confirmed enforced — renders the conservative (advisory) wording UNLESS
 * enforcement is positively confirmed; a fetch failure never gets upgraded into an unconfirmed
 * enforcement claim.
 */
export function GovernanceFindingsPanel({ projectId }: { projectId: string }) {
  const { session } = useAuth();
  const canResolve = canManageFleet(session?.role);
  const { data, isPending, isError, error, refetch } = useGovernanceFindings(projectId, {
    limit: PAGE_LIMIT,
  });
  const resolve = useResolveGovernanceFinding(projectId);
  const policyQuery = useProjectGovernancePolicy(projectId);

  const findings = data?.items;
  const openCount = findings?.filter((f) => f.status === "open").length ?? 0;

  // The banner's TRUTH: only claim ENFORCED once the policy fetch has SUCCEEDED and the
  // effective declared policy actually names `promote.require.max_open_findings`. While the
  // fetch is pending, has failed, or the project's own governance is declared-but-unparseable,
  // treat it as NOT confirmed enforced — the conservative advisory wording below is the honest
  // default in every one of those cases (never imply an enforcement guarantee this fetch
  // couldn't confirm).
  const policy = !policyQuery.isPending && !policyQuery.isError ? policyQuery.data : undefined;
  const enforcingPredicate = policy?.predicates.find((p) => p.id === MAX_OPEN_FINDINGS_PREDICATE_ID);
  const maxOpenFindings = enforcingPredicate?.values[0];

  return (
    <section className="governance-findings" aria-label="Governing-agent findings">
      <h2 className="governance-findings__title">Governing-agent findings</h2>
      <p className="governance-findings__advisory" role="note">
        {openCount > 0 ? `${openCount} open. ` : ""}
        {policy?.policyError ? (
          <>
            This project's declared governance is currently <strong>unparseable</strong> — every
            promotion is <strong>denied</strong> until it's fixed, regardless of open findings.
            Only a human resolves a finding — the governing agent can only ever raise one.
          </>
        ) : maxOpenFindings !== undefined ? (
          <>
            Findings are <strong>enforced</strong> for this project: its governance declares{" "}
            <code>promote.require.max_open_findings = {maxOpenFindings}</code>, so once open
            findings reach that count, promotion is <strong>blocked</strong>. Only a human resolves
            a finding — the governing agent can only ever raise one.
          </>
        ) : (
          <>
            Findings are <strong>advisory</strong> unless this project's governance declares{" "}
            <code>promote.require.max_open_findings</code>, which makes an open finding <strong>block</strong>{" "}
            promotion. Only a human resolves a finding — the governing agent can only ever raise one.
          </>
        )}
      </p>

      <QueryStates
        isLoading={isPending}
        loadingLabel="Loading governance findings…"
        isError={isError}
        error={error}
        onRetry={refetch}
        errorDescription={
          error instanceof Error
            ? error.message
            : "Couldn't load governance findings. Check your connection and try again."
        }
        isEmpty={Boolean(findings && findings.length === 0)}
        empty={
          <EmptyState
            title="No governance findings"
            description={
              "The governing agent has raised no objections against this project's promotions. " +
              "When a finding producer flags a change, it shows up here for a human to review and resolve."
            }
          />
        }
      >
        {findings && (
          <ul className="governance-findings__list">
            {findings.map((f) => (
              <FindingRow
                key={f.id}
                finding={f}
                canResolve={canResolve}
                resolving={resolve.isPending && resolve.variables?.id === f.id}
                onResolve={(id) => resolve.mutate({ id, note: "" })}
              />
            ))}
          </ul>
        )}
      </QueryStates>
    </section>
  );
}
